/**
 * Content → Sticking Patterns.
 *
 * The same filtered rows, two ways round:
 *   - Pattern pivot: which mistakes recur across the population, and how widely.
 *   - User pivot:    which students carry the most, and what leads for each.
 *
 * Both drill down to the evidence: pattern → carriers → their attempts, or
 * user → their patterns → their attempts, ending at the existing
 * `submissions/:id` analysis detail.
 *
 * See docs/FeatureDesigns/diagnosis/sticking-patterns-surfaces.md.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  SearchFilterBar, TabStrip, DataTable,
  type FilterDef, type ColumnDef, type SortState,
} from '@shared-ui/components';
import { type UserSearchOption } from '@shared-ui/user-picker';
import { ScopedUserSearchFilter } from '../../components/ScopedUserSearchFilter';
import { fetchWithAdmin } from '../../api/fetchHelper';
import type { ListPatternsResponse, PatternRollup, UserRollup } from './types';
import { UserStickingPatternsPanel } from './UserStickingPatternsPanel';
import { StickingPatternCarriersPanel } from './StickingPatternCarriersPanel';
import { DiagnosesPanel } from './DiagnosesPanel';
import { TentativeDiagnosesPanel } from './TentativeDiagnosesPanel';
import { formatWhen, spread } from './format';
import './StickingPatterns.css';

type Pivot = 'pattern' | 'user' | 'diagnoses' | 'tentative';

const SUBJECTS = ['maths', 'physics', 'chemistry', 'biology'];

/**
 * Sorting is client-side, over the rows already on the page.
 *
 * The list endpoint groups a capped scan of memory rows, so there is no total
 * ordering to ask the server for — and when `truncated` is set the page already
 * says so. Sorting a partial set is honest as long as the caller knows it is
 * partial, which that warning covers.
 */
function sortRows<T>(rows: T[], sort: SortState, value: (row: T, key: string) => number | string | undefined): T[] {
  const dir = sort.sortOrder === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = value(a, sort.sortBy);
    const bv = value(b, sort.sortBy);
    if (av === bv) return 0;
    if (av === undefined) return 1;      // missing values sink, either direction
    if (bv === undefined) return -1;
    return av < bv ? -dir : dir;
  });
}

export function StickingPatterns() {
  const [pivot, setPivot] = useState<Pivot>('pattern');
  const [picked, setPicked] = useState<UserSearchOption | null>(null);
  const [pickedEmail, setPickedEmail] = useState('');
  const [applied, setApplied] = useState<Record<string, string>>({
    subject: '', patternType: '', lastKind: '', trend: '',
    keyContains: '', labelContains: '', minRecurrence: '', minUsers: '',
    seenFrom: '', seenTo: '',
  });
  /** One sort per pivot — the two tables share no columns, so a single state
   *  would carry a meaningless sortBy across a tab switch. */
  /** Scope the page to one student, from a carriers row. Switches pivot too:
   *  the filter is applied to both views, but leaving the user on By-pattern
   *  would show a single-row table with no sign of why. */
  const filterByUser = (u: { userId: string; email: string; name?: string }) => {
    setPicked({ userId: u.userId, email: u.email, name: u.name });
    setPickedEmail(u.email);
    setPivot('user');
  };

  const [patternSort, setPatternSort] = useState<SortState>({ sortBy: 'userCount', sortOrder: 'desc' });
  const [userSort, setUserSort] = useState<SortState>({ sortBy: 'totalEncounters', sortOrder: 'desc' });

  /** One filter per stored field. The values are the ones in the documents —
   *  `concept` / `category`, `slip` / `gap`, `new` / `persistent` — so what an
   *  admin picks here is what they will match against in Cosmos. */
  const filterDefs: FilterDef[] = [
    { key: 'subject', kind: 'select', placeholder: 'All subjects', options: SUBJECTS.map(s => ({ value: s, label: s })) },
    {
      key: 'patternType', kind: 'select', placeholder: 'All patternType',
      options: [{ value: 'concept', label: 'concept' }, { value: 'category', label: 'category' }],
    },
    {
      key: 'lastKind', kind: 'select', placeholder: 'All lastKind',
      options: [{ value: 'slip', label: 'slip' }, { value: 'gap', label: 'gap' }, { value: 'none', label: 'none' }],
    },
    {
      key: 'trend', kind: 'select', placeholder: 'All trends',
      // `closing` / `resolved` exist in the type but are unreachable today —
      // they need a positive signal the gap-only diagnosis stream never emits.
      options: [{ value: 'new', label: 'new' }, { value: 'persistent', label: 'persistent' }],
    },
    { key: 'keyContains', kind: 'text', placeholder: 'patternKey contains' },
    { key: 'labelContains', kind: 'text', placeholder: 'label contains' },
    { key: 'minRecurrence', kind: 'text', placeholder: 'Min times (per student)' },
    { key: 'minUsers', kind: 'text', placeholder: 'Min students' },
    { key: 'seen', kind: 'daterange', label: 'Last seen', fromPlaceholder: 'from', toPlaceholder: 'to' },
  ];

  const userId = picked?.userId ?? '';
  const params = new URLSearchParams({ pivot });
  for (const [k, v] of Object.entries(applied)) {
    if (!v) continue;
    // The daterange filter stores `seenFrom`/`seenTo`; the endpoint takes
    // `since`/`until` against lastSeenAt.
    if (k === 'seenFrom') params.set('since', v);
    else if (k === 'seenTo') params.set('until', v);
    else params.set(k, v);
  }
  if (userId) params.set('userId', userId);

  const list = useQuery({
    queryKey: ['sticking-patterns', pivot, applied, userId],
    queryFn: () => fetchWithAdmin<ListPatternsResponse>(`/console/sticking-patterns?${params}`),
  });

  const data = list.data;

  const patternRows = useMemo(
    () => sortRows(data?.patterns ?? [], patternSort, (p, k) => (p as unknown as Record<string, number>)[k]),
    [data?.patterns, patternSort],
  );
  const userRows = useMemo(
    () => sortRows(data?.users ?? [], userSort, (u, k) => (u as unknown as Record<string, number>)[k]),
    [data?.users, userSort],
  );

  /** One column per stored field, rendered as stored. `cause` is deliberately
   *  absent: it is per-encounter free text, which is what the rollup aggregates
   *  over — it lives on the carriers panel, against the attempt that produced
   *  it. */
  const patternColumns: ColumnDef<PatternRollup>[] = [
    {
      key: 'patternKey', header: 'patternKey', mobile: 'title', sortable: true,
      render: p => <span className="lp-key">{p.patternKey}</span>,
    },
    {
      key: 'label', header: 'label',
      render: p => (
        <span className="lp-name">
          {p.label}
          {p.labelVariants > 0 && <span className="lp-dim"> +{p.labelVariants}</span>}
        </span>
      ),
    },
    {
      key: 'patternType', header: 'patternType',
      render: p => (
        <span className={`lp-chip ${p.patternType === 'category' ? 'is-habit' : 'is-gap'}`}>
          {p.patternType}
        </span>
      ),
    },
    {
      key: 'subjects', header: 'subject', className: 'lp-dim',
      render: p => (p.subjects.length ? p.subjects.join(', ') : '—'),
    },
    { key: 'kinds', header: 'lastKind', className: 'lp-dim', render: p => spread(p.kinds) },
    { key: 'userCount', header: 'students', sortable: true, render: p => p.userCount },
    { key: 'totalEncounters', header: 'recurrence', sortable: true, render: p => p.totalEncounters },
    { key: 'trends', header: 'trend', className: 'lp-dim', render: p => spread(p.trends) },
    {
      key: 'firstSeenAt', header: 'firstSeenAt', sortable: true, className: 'lp-dim',
      render: p => (p.firstSeenAt ? formatWhen(p.firstSeenAt) : '—'),
    },
    {
      key: 'lastSeenAt', header: 'lastSeenAt', sortable: true, className: 'lp-dim',
      render: p => (p.lastSeenAt ? formatWhen(p.lastSeenAt) : '—'),
    },
  ];

  const userColumns: ColumnDef<UserRollup>[] = [
    {
      key: 'userName', header: 'Student', mobile: 'title',
      render: u => (
        <div className="lp-namecol">
          <span className="lp-name">{u.userName ?? '—'}</span>
          <span className="lp-key">{u.userEmail ?? u.userId}</span>
        </div>
      ),
    },
    { key: 'patternCount', header: 'Patterns', sortable: true, render: u => u.patternCount },
    { key: 'conceptGaps', header: 'concept', sortable: true, render: u => u.conceptGaps },
    { key: 'habits', header: 'category', sortable: true, render: u => u.habits },
    { key: 'totalEncounters', header: 'Encounters', sortable: true, render: u => u.totalEncounters },
    {
      key: 'topPattern', header: 'Heaviest', className: 'lp-dim',
      render: u => (u.topPattern
        ? `${u.topPattern.label} (${u.topPattern.recurrenceCount})`
        : '—'),
    },
    {
      key: 'lastSeenAt', header: 'Last seen', sortable: true, className: 'lp-dim',
      render: u => (u.lastSeenAt ? formatWhen(u.lastSeenAt) : '—'),
    },
  ];

  return (
    <div className="lp-page">
      <div className="lp-header">
        <h1>Sticking Patterns</h1>
      </div>

      <TabStrip
        className="lp-tabs"
        ariaLabel="Sticking pattern views"
        active={pivot}
        onChange={setPivot}
        tabs={[
          { key: 'pattern', label: 'By pattern' },
          { key: 'user', label: 'By user' },
          { key: 'diagnoses', label: 'Diagnoses' },
          { key: 'tentative', label: 'Tentative' },
        ] as const}
      />

      {pivot === 'diagnoses' && (
        <>
          <p className="lp-muted lp-explainer">
            One row per analysis — the <code>diagnosis</code> the analyzer emitted, and what the
            learning memory did with it. The other two tabs aggregate these across students.
          </p>
          <DiagnosesPanel userId={picked?.userId} />
        </>
      )}

      {pivot === 'tentative' && (
        <>
          <p className="lp-muted lp-explainer">
            Diagnoses held back because the reading behind them is in doubt. Open one, look at the
            page, and say what is written — the claims resting on it are then used, or rejected.
          </p>
          <TentativeDiagnosesPanel />
        </>
      )}

      {pivot !== 'diagnoses' && pivot !== 'tentative' && (<>
      <SearchFilterBar
        filters={filterDefs}
        values={applied}
        onChange={setApplied}
        hideSearch
        // The user picker scopes the whole view rather than narrowing within
        // it, so it leads the row — see SearchFilterBar's `leading`.
        leading={(
          <ScopedUserSearchFilter
            value={pickedEmail}
            onChange={setPickedEmail}
            onPick={setPicked}
            placeholder="Filter by user…"
          />
        )}
      />

      {list.isLoading && <p className="lp-muted">Loading…</p>}
      {list.isError && <p className="lp-error">Failed: {(list.error as Error).message}</p>}

      {data && (
        <p className="lp-count">
          {pivot === 'pattern'
            ? `${data.patterns?.length ?? 0} patterns`
            : `${data.users?.length ?? 0} students`}
          {' '}from {data.scanned} memory rows
          {/* The scan cap is real; say when a page is partial rather than
              letting it read as the whole population. */}
          {data.truncated && <span className="lp-warn"> — capped, narrow the filters for a complete picture</span>}
        </p>
      )}

      {/* Patterns accrue from the diagnosis on analysed attempts, so an
          environment with few analysed mistakes will look empty. */}
      {pivot === 'pattern' && data?.patterns && (
        <DataTable
          className="lp-table"
          compact
          columns={patternColumns}
          data={patternRows}
          rowKey={p => `${p.patternType}:${p.patternKey}`}
          sort={patternSort}
          onSortChange={setPatternSort}
          emptyMessage="No sticking patterns match. Patterns accrue from the diagnosis on analysed attempts."
          renderExpandedRow={p => (
            <StickingPatternCarriersPanel
              patternType={p.patternType}
              patternKey={p.patternKey}
              onFilterUser={filterByUser}
            />
          )}
        />
      )}

      {pivot === 'user' && data?.users && (
        <DataTable
          className="lp-table"
          compact
          columns={userColumns}
          data={userRows}
          rowKey={u => u.userId}
          sort={userSort}
          onSortChange={setUserSort}
          emptyMessage="No students match. Patterns accrue from the diagnosis on analysed attempts."
          renderExpandedRow={u => <UserStickingPatternsPanel userId={u.userId} />}
        />
      )}
      </>)}
    </div>
  );
}

export default StickingPatterns;
